import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { PaperIconComponent, PaperKbdComponent, PaperSpotlightMarkComponent } from '@ricoh-paper/angular';
import type { CmdkNewsItem } from '../../models/cmdk.model';

/**
 * An article, announcement, video or knowledge page — or a Spotlight, which
 * carries the Spotlight mark in place of the type glyph (PLAN 7.4).
 *
 * The glyph is the only thing telling four quite different things apart in
 * one group — a two-minute announcement and a knowledge page look identical
 * otherwise — so it is driven off `newsType` rather than being decorative.
 *
 * No thumbnail. Articles carry a cover image, knowledge pages never do, and a
 * list where some rows have art and others have a grey square reads as broken
 * rather than as mixed content. The image belongs in the preview, where there
 * is room for it to be either present or absent without disturbing a rhythm.
 */
@Component({
  selector: 'im-cmdk-row-news',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, PaperKbdComponent, PaperSpotlightMarkComponent],
  templateUrl: './cmdk-row-news.component.html',
  styleUrl: './cmdk-row-news.component.scss',
})
export class CmdkRowNewsComponent {
  readonly item = input.required<CmdkNewsItem>();
  readonly selected = input(false);
  readonly quickOpenIndex = input<number | null>(null);

  protected readonly icon = computed(() => {
    switch (this.item().newsType) {
      case 'video': return 'play';
      case 'announcement': return 'megaphone';
      case 'page': return 'file-text';
      default: return 'newspaper';
    }
  });

  protected readonly quickOpenKey = computed(() => {
    const index = this.quickOpenIndex();
    return index === null ? null : String(index + 1);
  });
}
